import { Head } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/retention" },
  { title: "Housekeeping", href: "/admin/housekeeping" },
  { title: "7-Year Purge", href: "/admin/housekeeping/purge7yr" },
]

interface PurgeCase {
  id: number
  case_number: string | null
  hpi_name: string
  status: number
  open_date: string | null
  closed_date: string | null
  is_deleted: boolean
  num_contacts: number
  num_documents: number
  num_expenses: number
  num_gps: number
  num_reports: number
  num_schedules: number
  num_subjects: number
  num_time: number
  num_updates: number
}

interface Props {
  cases: PurgeCase[]
  total_eligible: number
}

function countCell(value: number) {
  return value > 0 ? value : ""
}

export default function Purge7yr({ cases, total_eligible }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="7-Year Purge" />

      <div className="p-6">
        <h1 className="border-hpi-light-gray mb-2 border-b pb-2 text-2xl font-semibold">
          7-Year Purge
        </h1>
        <p className="text-hpi-med-gray mb-4 text-sm">
          Delete all reports, time, expenses, updates, schedule, documents,
          subject data not needed for conflict check.
        </p>

        {total_eligible > cases.length && (
          <p className="mb-4 text-sm text-amber-700">
            Showing {cases.length} of {total_eligible} eligible cases.
          </p>
        )}

        {cases.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-3 py-2 text-left">
                  Case
                </th>
                <th className="border-hpi-light-gray border px-3 py-2 text-left">
                  Opened
                </th>
                <th className="border-hpi-light-gray border px-3 py-2 text-left">
                  Closed
                </th>
                <th className="border-hpi-light-gray border px-3 py-2 text-right">
                  Contacts
                </th>
                <th className="border-hpi-light-gray border px-3 py-2 text-right">
                  Docs
                </th>
                <th className="border-hpi-light-gray border px-3 py-2 text-right">
                  Exp
                </th>
                <th className="border-hpi-light-gray border px-3 py-2 text-right">
                  GPS
                </th>
                <th className="border-hpi-light-gray border px-3 py-2 text-right">
                  Rpt
                </th>
                <th className="border-hpi-light-gray border px-3 py-2 text-right">
                  Sch
                </th>
                <th className="border-hpi-light-gray border px-3 py-2 text-right">
                  Sub
                </th>
                <th className="border-hpi-light-gray border px-3 py-2 text-right">
                  Time
                </th>
                <th className="border-hpi-light-gray border px-3 py-2 text-right">
                  Upd
                </th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr key={c.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-3 py-2">
                    <a
                      href={`/cases/${c.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-hpi-active-blue hover:underline"
                    >
                      #{c.case_number ?? c.id} {c.hpi_name}
                    </a>
                    {c.status !== 2 && (
                      <span className="text-hpi-med-gray ml-1 text-xs">
                        ({c.status})
                      </span>
                    )}
                    {c.is_deleted && (
                      <span className="text-hpi-red ml-1" title="Deleted case">
                        &#x1f5d1;
                      </span>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-3 py-2 whitespace-nowrap">
                    {c.open_date}
                  </td>
                  <td className="border-hpi-light-gray border px-3 py-2 whitespace-nowrap">
                    {c.closed_date}
                  </td>
                  <td className="border-hpi-light-gray border px-3 py-2 text-right">
                    {countCell(c.num_contacts)}
                  </td>
                  <td className="border-hpi-light-gray border px-3 py-2 text-right">
                    {countCell(c.num_documents)}
                  </td>
                  <td className="border-hpi-light-gray border px-3 py-2 text-right">
                    {countCell(c.num_expenses)}
                  </td>
                  <td className="border-hpi-light-gray border px-3 py-2 text-right">
                    {countCell(c.num_gps)}
                  </td>
                  <td className="border-hpi-light-gray border px-3 py-2 text-right">
                    {countCell(c.num_reports)}
                  </td>
                  <td className="border-hpi-light-gray border px-3 py-2 text-right">
                    {countCell(c.num_schedules)}
                  </td>
                  <td className="border-hpi-light-gray border px-3 py-2 text-right">
                    {countCell(c.num_subjects)}
                  </td>
                  <td className="border-hpi-light-gray border px-3 py-2 text-right">
                    {countCell(c.num_time)}
                  </td>
                  <td className="border-hpi-light-gray border px-3 py-2 text-right">
                    {countCell(c.num_updates)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">
            No cases are currently eligible for 7-year data purge.
          </p>
        )}
      </div>
    </AppLayout>
  )
}
